import {
    Box, IconButton, Typography, Menu, MenuItem, ListItemIcon, ListItemText, Divider,
    useMediaQuery, useTheme, Button,
} from "@mui/material";
import {
    ArrowForward,
    ManageAccounts as ManageAccountsIcon,
    EditOutlined,
    Palette as PaletteIcon,
    CardMembership as SubscriptionIcon,
    ToggleOn,
    ToggleOff,
    DeleteOutline,
    LocationOn as LocationIcon,
    FitnessCenter as TrainersIcon,
    Groups as ClientsIcon,
} from "@mui/icons-material";
import {useNavigate, useParams} from "react-router-dom";
import AddCompanyDialog from "../componenets/company/AddCompanyDialog";
import {ReactComponent as ImageSkeletonIcon} from "../assets/images/image-skelaton.svg";
import {ReactComponent as EditImageIcon} from "../assets/images/editProfile.svg";
import CompanyManagersSection from "../componenets/company/CompanyManagersSection";
import CompanyStatusSection from "../componenets/company/CompanyStatusSection";
import CompanyBrandingSection from "../componenets/company/CompanyBrandingSection";
import CompanySettingsSection from "../componenets/company/CompanySettingsSection";
import {ConfirmDialog, StatusChip} from "../componenets/ui";
import {useEffect, useState} from "react";
import {
    deleteCompany,
    disableComapny,
    enableCompany,
    getCompany,
    uploadImage,
    updateSubscriptionPlan
} from "../services/system/CompanyService";
import Loader from "../componenets/Loader";
import {useMessageContext} from "../contexts/MessageContext";
import {getErrorMessage} from "../enums/ErrorMessages";
import {getApiErrorCode} from "../utils/apiError";
import {colors, borders, shadows} from "../componenets/ui/colors";
import {ReactComponent as ActiveSvg} from "../assets/images/active.svg";
import {ReactComponent as InactiveSvg} from "../assets/images/inactive.svg";
import {getSubscriptionPlanName, subscriptionPlans} from "../enums/SubscriptionPlans";

const InfoPill = ({icon: Icon, label, value}) => (
    <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
        <Icon sx={{color: colors.textMuted, fontSize: 16}}/>
        <Typography sx={{fontSize: "12px", fontWeight: 500, color: colors.textMuted}}>{label}:</Typography>
        <Typography sx={{fontSize: "12px", fontWeight: 600, color: colors.textSecondary}}>{value}</Typography>
    </Box>
);

const CompanyPage = () => {
    const navigate = useNavigate();
    const params = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [company, setCompany] = useState();
    const [loading, setLoading] = useState(false);
    const [loadCompany, setLoadCompany] = useState(true);

    const {notificationHandler} = useMessageContext();

    // Menu state
    const [menuAnchor, setMenuAnchor] = useState(null);
    const menuOpen = Boolean(menuAnchor);
    const [deleteOpen, setDeleteOpen] = useState(false);

    // Subscription sub-menu
    const [subMenuAnchor, setSubMenuAnchor] = useState(null);
    const subMenuOpen = Boolean(subMenuAnchor);

    // Dialog states
    const [editCompanyOpen, setEditCompanyOpen] = useState(false);
    const [editBrandingOpen, setEditBrandingOpen] = useState(false);

    useEffect(() => {
        getCompany(params.companyId).then(res => {
            setCompany(res.data);
        }).catch((e) => {
            notificationHandler.error(getErrorMessage(getApiErrorCode(e)));
        });
    }, [loadCompany]);

    const handleEnableDisableCompany = () => {
        const action = company.metaData.enabled ? disableComapny : enableCompany;
        const msg = company.metaData.enabled ? "חברה בוטלה בהצלחה" : "חברה הופעלה בהצלחה";
        action(params.companyId)
            .then(() => notificationHandler.success(msg))
            .then(() => setLoadCompany(prv => !prv))
            .catch(e => notificationHandler.error(getErrorMessage(getApiErrorCode(e))));
    };

    const handleDeleteCompany = () => {
        deleteCompany(params.companyId)
            .then(() => navigate('/app/companies'))
            .then(() => notificationHandler.success("חברה נמחקה בהצלחה"))
            .catch(e => notificationHandler.error(getErrorMessage(getApiErrorCode(e))));
    };

    const handleChangeSubscription = (plan) => {
        updateSubscriptionPlan(company.metaData.id, plan)
            .then(() => {
                notificationHandler.success("מסלול עודכן בהצלחה");
                setLoadCompany(prv => !prv);
            })
            .catch(e => notificationHandler.error(getErrorMessage(getApiErrorCode(e))));
        setSubMenuAnchor(null);
        setMenuAnchor(null);
    };

    const handleFileUpload = (e) => {
        setLoading(true);
        const file = e.target.files[0];
        uploadImage(company.metaData.id, file)
            .then(() => {
                setLoading(false);
                navigate(0);
            });
    };

    const meta = company?.metaData;

    const menuItems = [
        {label: "ערוך פרטי חברה", icon: <EditOutlined fontSize="small"/>, onClick: () => { setEditCompanyOpen(true); setMenuAnchor(null); }},
        {label: "ערוך מיתוג", icon: <PaletteIcon fontSize="small"/>, onClick: () => { setEditBrandingOpen(true); setMenuAnchor(null); }},
        {label: "שנה מסלול", icon: <SubscriptionIcon fontSize="small"/>, onClick: (e) => { setSubMenuAnchor(e.currentTarget); }},
        {divider: true},
        {
            label: meta?.enabled ? "חסום חברה" : "הפעל חברה",
            icon: meta?.enabled ? <ToggleOff fontSize="small"/> : <ToggleOn fontSize="small"/>,
            onClick: () => { handleEnableDisableCompany(); setMenuAnchor(null); },
            color: meta?.enabled ? colors.error : colors.success,
        },
        {divider: true},
        {
            label: "מחק חברה",
            icon: <DeleteOutline fontSize="small"/>,
            onClick: () => { setDeleteOpen(true); setMenuAnchor(null); },
            color: colors.error,
        },
    ];

    return (
        <Box sx={{display: "flex", flexDirection: "column", height: "100%", bgcolor: colors.bg}}>
            <Loader loading={loading}/>

            {/* ─── Header ──────────────────────────────────── */}
            <Box sx={{
                bgcolor: colors.white, borderBottom: borders.default,
                px: {xs: 2, md: 3}, pt: {xs: 1.5, md: 2}, pb: {xs: 1, md: 1.5},
                flexShrink: 0, direction: "rtl",
            }}>
                {/* Title row */}
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 1}}>
                    <IconButton
                        onClick={() => navigate('/app/companies')}
                        sx={{color: colors.textSecondary, p: 1, "&:hover": {bgcolor: colors.bgHover, color: colors.textPrimary}}}
                    >
                        <ArrowForward/>
                    </IconButton>

                    <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2}}>
                        {meta?.name || "פרטי חברה"}
                    </Typography>

                    {meta && (
                        meta.enabled
                            ? <StatusChip label="פעילה" variant="success" icon={<ActiveSvg style={{width: 11, height: 11}}/>} size="small"/>
                            : <StatusChip label="חסומה" variant="error" icon={<InactiveSvg style={{width: 11, height: 11}}/>} size="small"/>
                    )}

                    <Box sx={{flex: 1}}/>

                    {/* 3-dot ActionMenu */}
                    {meta && (
                        <>
                            <IconButton
                                onClick={e => setMenuAnchor(e.currentTarget)}
                                sx={{
                                    color: colors.textSecondary, border: borders.default,
                                    borderRadius: borders.radius.md, width: 40, height: 40,
                                    "&:hover": {bgcolor: colors.bgHover, borderColor: colors.borderHover},
                                }}
                            >
                                <ManageAccountsIcon/>
                            </IconButton>
                            <Menu
                                anchorEl={menuAnchor} open={menuOpen}
                                onClose={() => setMenuAnchor(null)}
                                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                                transformOrigin={{vertical: "top", horizontal: "left"}}
                                PaperProps={{sx: {
                                    borderRadius: borders.radius.lg, border: borders.default,
                                    boxShadow: shadows.dropdown, mt: 0.5, minWidth: 200,
                                }}}
                            >
                                {menuItems.map((item, i) =>
                                    item.divider ? (
                                        <Divider key={`d-${i}`} sx={{my: 0.5}}/>
                                    ) : (
                                        <MenuItem
                                            key={item.label}
                                            onClick={item.onClick}
                                            sx={{
                                                fontSize: "14px", fontWeight: 500, py: 1.25, px: 2,
                                                mx: 0.75, borderRadius: borders.radius.sm,
                                                color: item.color || colors.textPrimary,
                                                "&:hover": {
                                                    bgcolor: item.color ? `${item.color}10` : colors.primarySubtle,
                                                    color: item.color || colors.primary,
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{color: "inherit", minWidth: 32}}>{item.icon}</ListItemIcon>
                                            <ListItemText primaryTypographyProps={{fontSize: "14px", fontWeight: 500}}>{item.label}</ListItemText>
                                        </MenuItem>
                                    )
                                )}
                            </Menu>

                            {/* Subscription sub-menu */}
                            <Menu
                                anchorEl={subMenuAnchor}
                                open={subMenuOpen}
                                onClose={() => setSubMenuAnchor(null)}
                                anchorOrigin={{vertical: "top", horizontal: "right"}}
                                transformOrigin={{vertical: "top", horizontal: "left"}}
                                PaperProps={{sx: {
                                    borderRadius: borders.radius.lg, border: borders.default,
                                    boxShadow: shadows.dropdown, minWidth: 220,
                                }}}
                            >
                                {subscriptionPlans.map((plan) => (
                                    <MenuItem
                                        key={plan.value}
                                        onClick={() => handleChangeSubscription(plan.value)}
                                        selected={meta?.subscriptionPlan === plan.value}
                                        sx={{
                                            fontSize: "13px", fontWeight: 500, py: 1, px: 2,
                                            mx: 0.75, borderRadius: borders.radius.sm,
                                            color: colors.textPrimary,
                                            "&.Mui-selected": {
                                                bgcolor: colors.primarySubtle,
                                                color: colors.primary,
                                                fontWeight: 600,
                                            },
                                            "&:hover": {
                                                bgcolor: colors.primarySubtle,
                                                color: colors.primary,
                                            },
                                        }}
                                    >
                                        {plan.name}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}
                </Box>

                {/* Quick info pills */}
                {meta && (
                    <Box sx={{display: "flex", gap: {xs: 1.5, md: 3}, flexWrap: "wrap", pr: 6.5}}>
                        {meta.address && <InfoPill icon={LocationIcon} label="מיקום" value={meta.address}/>}
                        <InfoPill icon={SubscriptionIcon} label="מסלול" value={getSubscriptionPlanName(meta.subscriptionPlan)}/>
                        {company.statistics && <InfoPill icon={TrainersIcon} label="מאמנים" value={company.statistics.numberOfTrainers || 0}/>}
                        {company.statistics && <InfoPill icon={ClientsIcon} label="לקוחות" value={company.statistics.numberOfTrainees || 0}/>}
                    </Box>
                )}
            </Box>

            {/* ─── Content ─────────────────────────────────── */}
            <Box sx={{flex: 1, overflow: "auto", px: {xs: 1.5, md: 3}, pb: 3, pt: 2}}>
                {/* Company Image */}
                <Box sx={{position: "relative", mb: {xs: 2, md: 3}}}>
                    <Button
                        variant="text"
                        component="label"
                    >
                        {meta?.imageUrl ? <img
                            width={isMobile ? 80 : 116}
                            height={isMobile ? 80 : 116}
                            src={meta.imageUrl}
                            alt=""
                            loading="lazy"
                            style={{borderRadius: "8px"}}
                        /> : <ImageSkeletonIcon style={{width: isMobile ? 80 : 116, height: isMobile ? 80 : 116}}/>}
                        <input
                            accept="image/png, image/gif, image/jpeg"
                            onChange={handleFileUpload}
                            type="file"
                            hidden
                        />
                        <Box sx={{
                            position: "absolute",
                            top: "80%",
                            right: {xs: "60px", md: "98px"},
                            cursor: "pointer"
                        }}>
                            <EditImageIcon style={{width: isMobile ? 20 : 24, height: isMobile ? 20 : 24}}/>
                        </Box>
                    </Button>
                </Box>

                {/* Section Cards */}
                {company && <CompanyBrandingSection setLoadCompany={setLoadCompany} company={meta} editOpen={editBrandingOpen} setEditOpen={setEditBrandingOpen}/>}
                <Box sx={{marginTop: {xs: "16px", md: "20px"}}}>
                    {company && <CompanySettingsSection company={meta} setLoadCompany={setLoadCompany}/>}
                </Box>
                <Box sx={{marginTop: {xs: "16px", md: "20px"}}}>
                    {company && <CompanyManagersSection compantId={meta.id} setLoadCompany={setLoadCompany} companyOwners={company.companyOwners}/>}
                </Box>
                <Box sx={{marginTop: {xs: "16px", md: "20px"}}}>
                    {company && <CompanyStatusSection company={meta} statistics={company.statistics}/>}
                </Box>
            </Box>

            {/* ─── Dialogs ─────────────────────────────────── */}
            {meta && (
                <>
                    <AddCompanyDialog
                        setReload={setLoadCompany}
                        open={editCompanyOpen}
                        close={() => setEditCompanyOpen(false)}
                        company={meta}
                    />
                    <ConfirmDialog
                        open={deleteOpen}
                        onClose={() => setDeleteOpen(false)}
                        onConfirm={handleDeleteCompany}
                        title="מחיקת חברה"
                        description={`האם למחוק את ${meta.name}? פעולה זו אינה ניתנת לביטול.`}
                        confirmText="מחק"
                        variant="danger"
                    />
                </>
            )}
        </Box>
    );
};

export default CompanyPage;
